import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  Rating,
  TextField,
  Button
} from '@mui/material';
import { GRADE_SELF_REPORTED_RESULT } from 'api/graphql/play';
import { createSnack } from 'common/utils/general';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import uiSlice from 'store/modules/ui';

/**
 * OTRIX Play — diálogo reutilizable para que entrenador/admin califique (1..5 + feedback)
 * un resultado auto-reportado. Refresca las queries que le pase el llamador.
 */
function GradeResultDialog({ open, result = null, refetchQueries = [], onClose }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (result) {
      setRating(result.rating || 0);
      setFeedback(result.feedback || '');
    }
  }, [result]);

  const [grade, { loading }] = useMutation(GRADE_SELF_REPORTED_RESULT, { refetchQueries });

  const toast = (message, variant = 'success') =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `grade-${Date.now()}`, variant, message })));

  const handleSave = async () => {
    if (!result || !rating) return;
    try {
      await grade({ variables: { id: result.id, rating, feedback: feedback || null } });
      toast(t('moderation.graded', { ns: 'play', defaultValue: 'Calificación guardada' }));
      onClose();
    } catch {
      toast(t('moderation.error', { ns: 'play', defaultValue: 'Ocurrió un error' }), 'error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {t('moderation.gradeTitle', { ns: 'play', defaultValue: 'Calificar resultado' })}
        <Typography variant="caption" color="text.secondary" display="block">
          {result?.exercise?.localized_name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
              {t('moderation.ratingLabel', { ns: 'play', defaultValue: 'Calificación' })}
            </Typography>
            <Rating value={rating} max={5} onChange={(_, v) => setRating(v || 0)} sx={{ color: 'gold.main' }} />
          </Box>
          <TextField
            label={t('moderation.feedbackLabel', { ns: 'play', defaultValue: 'Comentario (opcional)' })}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}>
          {t('moderation.cancel', { ns: 'play', defaultValue: 'Cancelar' })}
        </Button>
        <Button onClick={handleSave} disabled={loading || !rating} sx={{ ...premiumButtonSx, px: 3, py: 1 }}>
          {t('moderation.saveGrade', { ns: 'play', defaultValue: 'Guardar' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

GradeResultDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  result: PropTypes.object,
  refetchQueries: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired
};

export default GradeResultDialog;
